// ============================================================================
// errors.ts — приведение ошибок ConnectRPC к виду для пользователя.
// connect-go формирует сообщение как "[<code>] <текст>" — код (unauthenticated,
// internal и т.п.) пользователю не нужен, показываем только текст.
// СЕТЕВЫЕ ошибки (сервер недоступен: "Failed to fetch" и т.п.) — человеческим
// сообщением: технические детали пользователю не показываются (детерминизм,
// никаких «failed to fetch» в интерфейсе).
// ============================================================================

import { translate } from '@/lib/i18n/store';

// Признаки сетевых ошибок (браузерный fetch / connect transport).
const NETWORK_HINTS = [
  'failed to fetch',
  'networkerror',
  'fetch failed',
  'load failed',
  'network request failed',
  'connection refused',
  'econnrefused',
  'err_connection',
  'net::err',
  'unavailable',
  'timeout',
  'timed out',
  'aborted',
  'abort error',
];

export function isNetworkError(e: unknown): boolean {
  const msg = (e instanceof Error ? e.message : String(e || '')).toLowerCase();
  return NETWORK_HINTS.some((h) => msg.includes(h));
}

// ============================================================================
// КЛАССИФИКАЦИЯ ОШИБОК — ЕДИНСТВЕННАЯ точка (SSoT/SRP): «транспортный сбой»
// (связь с сервером потеряна: fetch не дошёл, таймаут, 502/503/504 от прокси)
// ОТЛИЧАЕТСЯ от «серверного ответа» (валидация/права/5xx с телом) — серверные
// ошибки обрабатываются КАК РАНЬШЕ: онлайн-ошибка бросается и показывается.
// Транспортный сбой → офлайн-путь: чтение из кэша, запись в очередь SyncEngine.
// ============================================================================

// Connect/gRPC коды ТРАНСПОРТА: DeadlineExceeded = 4, Unavailable = 14
// (connect-es мапит HTTP 429/502/503/504 на Unavailable — прокси десктопа/
// nginx отдают 502, когда апстрим недоступен).
const TRANSPORT_CODES = [4, 14];
const TRANSPORT_CODE_NAMES = ['deadline_exceeded', 'deadlineexceeded', 'unavailable'];

// Признаки в ТЕКСТЕ, которые транспорт генерирует САМ (у серверного ответа
// такого текста нет): HTTP-статусы прокси и ошибка разбора НЕ-JSON тела
// прокси (connect-web делает response.json() на text/plain тело — SyntaxError
// «... is not valid JSON»; иначе 502 десктопа не распознался бы).
const TRANSPORT_MESSAGE_HINTS = [
  'http 502',
  'http 503',
  'http 504',
  'bad gateway',
  'gateway timeout',
  'service unavailable',
  'is not valid json',
  'missing trailer',
];

/**
 * Транспортный сбой (связь потеряна) — офлайн-путь; НЕ серверная ошибка.
 * Исключение: явные серверные ответы с этим кодом (режим обслуживания) —
 * показываются как есть, в очередь НЕ уходят.
 */
export function isTransportError(e: unknown): boolean {
  const msg = (e instanceof Error ? e.message : String(e || '')).toLowerCase();
  // «Режим обслуживания» — серверный ОТВЕТ (Unavailable с телом) — не транспорт.
  if (msg.includes('режим обслуживания')) return false;
  const c = errorCode(e);
  if (typeof c === 'number' && TRANSPORT_CODES.includes(c)) return true;
  if (typeof c === 'string') {
    const s = c.toLowerCase();
    if (TRANSPORT_CODES.includes(Number(s))) return true;
    if (TRANSPORT_CODE_NAMES.includes(s)) return true;
  }
  if (isNetworkError(e)) return true;
  return TRANSPORT_MESSAGE_HINTS.some((h) => msg.includes(h));
}

export function friendlyError(e: unknown): string {
  if (isNetworkError(e)) {
    return translate('errors.network_unavailable');
  }
  const msg = e instanceof Error ? e.message : String(e || translate('common.error'));
  return msg.replace(/^\[[a-z_ ]+\]\s*/i, '');
}

// ============================================================================
// Определение КОДА ошибки ConnectRPC (SSoT/SRP, единая точка).
// connect-es ConnectError.code — ЧИСЛОВОЙ enum (Code.NotFound = 5), а не строка;
// connect-go в тексте — "[not_found] ...". Поэтому проверяем ВСЕ формы:
//   число (5) | строку ('not_found'/'NotFound') | текст ('not found'/'не найдена').
// ============================================================================
export function errorCode(e: unknown): number | string | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const anyE = e as Record<string, unknown>;
  const c = anyE['code'];
  if (typeof c === 'number' || typeof c === 'string') return c;
  // connect-go: код в начале message "[not_found] ..."
  if (typeof anyE['message'] === 'string') {
    const m = /^\[([a-z_ ]+)\]\s*/.exec(anyE['message'] as string);
    if (m) return m[1];
  }
  return undefined;
}

/** NotFound (404): connect Code.NotFound = 5, текст "[not_found]" / "not found". */
export function isNotFoundError(e: unknown): boolean {
  const c = errorCode(e);
  if (c === 5 || c === 'not_found' || c === 'NotFound') return true;
  const msg = (e instanceof Error ? e.message : String(e || '')).toLowerCase();
  return msg.includes('not found') || msg.includes('не найдена') || msg.includes('не найден');
}

/** PermissionDenied (403): connect Code.PermissionDenied = 7 / "[permission_denied]". */
export function isPermissionDeniedError(e: unknown): boolean {
  const c = errorCode(e);
  if (c === 7 || c === 'permission_denied' || c === 'PermissionDenied') return true;
  const msg = (e instanceof Error ? e.message : String(e || '')).toLowerCase();
  return msg.includes('[permission_denied]') || msg.includes('нет права');
}

/** Техническая деталь ошибки (для журнала регистрации, НЕ для пользователя). */
export function describeError(e: unknown): string {
  if (e instanceof Error) {
    const parts = [e.message];
    if (e.name && e.name !== 'Error') parts.unshift(e.name);
    if (e.stack) {
      const first = e.stack.split('\n').find((l) => l.includes('at '));
      if (first) parts.push(first.trim());
    }
    return parts.join(' | ');
  }
  return String(e || '');
}

/**
 * Журналирование ошибки в журнал регистрации (категория ui, level error):
 * пользователю — человеческий текст, в журнал — техническая деталь.
 * Сервер недоступен → событие уходит в durable-очередь (IndexedDB) и
 * появляется в журнале после восстановления подключения (SSoT — сервер).
 */
export function logErrorToJournal(e: unknown, context: string): void {
  try {
    const detail = describeError(e);
    // eventLog отдельно отображает details; level error; категория ui
    void import('./eventLog').then((m) => {
      m.eventLog.error('ui', `${context}: ${friendlyError(e)}`, { details: detail });
    });
  } catch {
    // журналирование не должно ронять приложение
  }
}

// ============================================================================
// ПРОГЛОЧЕННЫЕ ОШИБКИ (2026-10-15) — «проглоченных ошибок быть не должно»:
// любой catch без обработки (фоновые операции, предзагрузки, необязательные
// шаги) обязан ОСТАВИТЬ СЛЕД в журнале регистрации. Поведение catch при этом
// СОХРАНЯЕТСЯ (ошибка не показывается пользователю) — журнал, а не тост.
// Анти-спам: один контекст не чаще раза в 60 секунд (циклы/ретраи не заливают
// журнал); повторные подавления считаются и попадают в details следующей записи.
// ============================================================================

const SWALLOW_ANTISPAM_MS = 60_000;
const swallowedRecent = new Map<string, { at: number; suppressed: number }>();

/**
 * Зафиксировать ПРОГЛОЧЕННУЮ ошибку в журнале (level warning, категория ui).
 * Вызывается из catch, где раньше было пусто (``catch {}`` / ``.catch(() => {})``).
 * `context` — «Файл:строка» или смысловое имя операции.
 */
export function logSwallowed(e: unknown, context: string): void {
  try {
    const now = Date.now();
    const prev = swallowedRecent.get(context);
    if (prev && now - prev.at < SWALLOW_ANTISPAM_MS) {
      prev.suppressed += 1;
      return;
    }
    const suppressed = prev ? prev.suppressed : 0;
    swallowedRecent.set(context, { at: now, suppressed: 0 });
    const detail =
      describeError(e) + (suppressed > 0 ? ` | подавлено повторов: ${suppressed}` : '');
    void import('./eventLog').then((m) => {
      m.eventLog.warning('ui', `Проглочена ошибка: ${context}`, { details: detail });
    });
  } catch {
    // журналирование не должно ронять приложение
  }
}
